/**
 * Regra de CONVERGÊNCIA / cobertura de fibra — lógica PURA (sem UI, sem fetch).
 *
 * Convergente = CNPJ com MÓVEL e FIXA (qualquer combinação novo/base). "Fibra" inclui
 * FIBRA e TT FIXA. A decisão olha o CNPJ inteiro (pedidos são divididos por família).
 *
 * Árvore travada com o gestor (Douglas):
 *  1. móvel + fibra              → passa (CONVERGENTE)
 *  2. só móvel + COM cobertura   → TRAVA (modal: cliente já tem fibra? senão → gerente)
 *  3. só móvel + SEM cobertura   → passa (SO_MOVEL, sem oportunidade)
 *  4. só móvel + cobertura nula  → precisa garantir o CEP antes de decidir
 *  5. só fibra                   → TRAVA (modal: informa móvel existente? senão → gerente)
 *  6. nem móvel nem fibra        → passa (NENHUM)
 * Fibra vendida p/ CNPJ cujo AACE móvel é de OUTRO parceiro → solicitação de carteira.
 */

export type StatusConvergencia =
  | 'aprovado com fibra'
  | 'aprovado sem fibra'
  | 'reprovado gerente'
  | 'reprovado supervisor'
  | 'solicitação de carteira'
  | 'pendente_supervisor'
  | 'pendente_gerente';

// HISTÓRICO de todas as escritas do ciclo NÃO vive aqui — vive na timeline
// `movimento` (registrarTimelineConvergencia). Aqui guardamos só o ESTADO ATUAL.

export type ClassificacaoConvergencia = 'CONVERGENTE' | 'SO_MOVEL' | 'SO_FIBRA' | 'NENHUM';

/** Saída da RPC convergencia_cobertura_cnpj. */
export interface CoberturaCnpj {
  temCobertura: boolean | null; // null = desconhecido (CNPJ sem CEP no cache)
  cep: string | null;
  territorioAace: string | null; // nome do parceiro/AACE da base móvel
  territorioNosso: boolean | null; // o AACE é o nosso (EMPRESARIAL_PRIME)?
  qtdLinhasVoz: number | null; // nº de linhas móvel do cliente
}

/** Fatos do CNPJ (somando o pedido atual + o que o CNPJ já tem). */
export interface FatosCnpj {
  temMovel: boolean;
  temFibra: boolean; // FIBRA ou TT FIXA
  movelNovoBase?: 'NOVO' | 'BASE' | null;
  fixaNovoBase?: 'NOVO' | 'BASE' | 'CONTRATO_EXISTENTE' | null;
  cnpjCategoria?: 'NOVO' | 'BASE' | 'MISTO' | null;
}

export type AcaoGate = 'passa' | 'trava_movel_sem_fibra' | 'trava_so_fibra' | 'precisa_cep';

export interface ResultadoGate {
  acao: AcaoGate;
  classificacao: ClassificacaoConvergencia;
}

/** Decisão no clique "Enviar ao BKO" (antes de qualquer modal). */
export function avaliarConvergenciaGate(f: FatosCnpj, cob: CoberturaCnpj): ResultadoGate {
  if (f.temMovel && f.temFibra) return { acao: 'passa', classificacao: 'CONVERGENTE' };

  if (f.temMovel && !f.temFibra) {
    if (cob.temCobertura === true) return { acao: 'trava_movel_sem_fibra', classificacao: 'SO_MOVEL' };
    if (cob.temCobertura === false) return { acao: 'passa', classificacao: 'SO_MOVEL' };
    return { acao: 'precisa_cep', classificacao: 'SO_MOVEL' }; // cobertura desconhecida
  }

  if (!f.temMovel && f.temFibra) return { acao: 'trava_so_fibra', classificacao: 'SO_FIBRA' };

  return { acao: 'passa', classificacao: 'NENHUM' };
}

// ── Resolução do modal: MÓVEL com cobertura, sem fibra ────────────────────────
export type RespostaMovelSemFibra =
  | { tipo: 'cliente_tem_fibra'; numeroContrato: string; endereco: string; documento: 'CNPJ'; provaUrls: string[] }
  | { tipo: 'cliente_tem_fibra'; numeroContrato: string; endereco: string; documento: 'CPF'; cpf: string; nome: string; provaUrls: string[] }
  | { tipo: 'enviar_gerente'; motivo: string; motivoTipo?: 'nao_venda_comercial' | 'sem_viabilidade_tecnica'; provaUrl?: string | null; provaUrls?: string[]; confirmouRf?: boolean };

export interface FixaJaExistente {
  tipoDocumento: 'CPF' | 'CNPJ';
  numeroContrato: string;
  endereco: string;
  cpf?: string | null;
  nome?: string | null;
}

export interface ResolucaoModal {
  acao: 'passa' | 'gerente';
  status: StatusConvergencia;
  classificacao: ClassificacaoConvergencia;
  fixaJaExistente?: FixaJaExistente | null;
  qtdLinhasMovel?: number | null;
  solicitacaoCarteira?: boolean;
  motivo?: string | null;
  motivoTipo?: 'nao_venda_comercial' | 'sem_viabilidade_tecnica' | 'declaracao_cliente';
  /** Print(s) "sem cobertura" anexado(s) pelo consultor (caso não-viabilidade). */
  provaUrl?: string | null;     // primeiro (compat)
  provaUrls?: string[];         // todos
  /** Consultor confirmou que o print é do endereço da Receita. */
  confirmouRf?: boolean;
}

export function resolverMovelSemFibra(r: RespostaMovelSemFibra): ResolucaoModal {
  if (r.tipo === 'cliente_tem_fibra') {
    const fixaJaExistente: FixaJaExistente =
      r.documento === 'CPF'
        ? { tipoDocumento: 'CPF', numeroContrato: r.numeroContrato, endereco: r.endereco, cpf: r.cpf, nome: r.nome }
        : { tipoDocumento: 'CNPJ', numeroContrato: r.numeroContrato, endereco: r.endereco };
    // Convergente (CPF não ganha o bônus, mas conta como convergência). Passa sem gerente.
    //
    // (01/09/2026) A PROVA vai junto. Auditoria de agosto: 861 declarações de
    // "o cliente já tem fibra" desde junho, 394 validadas pelo BKO e ZERO com
    // print anexado — a validação inteira se apoiava no número do contrato
    // digitado dos dois lados. Isso responde por 31% do bônus de convergência
    // (R$ 6.727 de base em agosto). Douglas: "tornar a prova real, com número
    // do pedido e print, e mesmo assim ir para aprovação da BKO responsável".
    const provas = r.provaUrls ?? [];
    return {
      acao: 'passa', status: 'aprovado com fibra', classificacao: 'CONVERGENTE', fixaJaExistente,
      motivoTipo: 'declaracao_cliente',
      provaUrl: provas[0] ?? null, provaUrls: provas,
    };
  }
  const urls = r.provaUrls ?? (r.provaUrl ? [r.provaUrl] : []);
  return { acao: 'gerente', status: 'aprovado sem fibra', classificacao: 'SO_MOVEL', motivo: r.motivo, motivoTipo: r.motivoTipo ?? (urls.length ? 'sem_viabilidade_tecnica' : 'nao_venda_comercial'), provaUrl: urls[0] ?? null, provaUrls: urls, confirmouRf: r.confirmouRf ?? false };
}

// ── Resolução do modal: SÓ FIBRA (sem móvel) ──────────────────────────────────
export type RespostaSoFibra =
  | { tipo: 'cliente_tem_movel'; qtdLinhas: number; territorio: string }
  | { tipo: 'enviar_gerente'; motivo: string };

export function resolverSoFibra(r: RespostaSoFibra, _cob: CoberturaCnpj): ResolucaoModal {
  // ⚠️ "Solicitação de carteira por fibra fora do território" NÃO EXISTE
  // (Douglas 18/08/2026: "isso não existe, não faz sentido"). Era um desvio de
  // desenho que NUNCA aconteceu — zero pedidos com esse status no banco desde
  // sempre. Declarou que o cliente tem móvel → é convergente, ponto.
  if (r.tipo === 'cliente_tem_movel') {
    return {
      acao: 'passa',
      status: 'aprovado com fibra',
      classificacao: 'CONVERGENTE',
      qtdLinhasMovel: r.qtdLinhas,
      solicitacaoCarteira: false,
    };
  }
  return { acao: 'gerente', status: 'aprovado sem fibra', classificacao: 'SO_FIBRA', motivo: r.motivo, solicitacaoCarteira: false };
}

// ── Montagem do JSONB que vai em pedidos.convergencia ─────────────────────────
export interface ConvergenciaPayload {
  classificacao: ClassificacaoConvergencia;
  status: StatusConvergencia | null;
  /** Justificativa escrita pelo consultor ao enviar p/ gerente (antes era perdida). */
  motivo: string | null;
  motivo_tipo?: 'nao_venda_comercial' | 'sem_viabilidade_tecnica' | 'declaracao_cliente' | 'automatico' | null;
  tem_movel: boolean;
  tem_fibra: boolean;
  cobertura_fibra: boolean | null;
  cep: string | null;
  territorio_aace: string | null;
  territorio_nosso: boolean | null;
  qtd_linhas_movel: number | null;
  cnpj_categoria: 'NOVO' | 'BASE' | 'MISTO' | null;
  movel_novo_base: 'NOVO' | 'BASE' | null;
  fixa_novo_base: 'NOVO' | 'BASE' | 'CONTRATO_EXISTENTE' | null;
  fixa_ja_existente: FixaJaExistente | null;
  decidido_por: string | null;
  decidido_em: string; // ISO
  origem_decisao: 'auto' | 'modal_cliente_tem_fibra' | 'modal_informou_movel' | 'consultor' | 'supervisor' | 'gerente';
  /** Validação do BKO para convergências AUTO-DECLARADAS pelo consultor
   *  ("já tem fibra/móvel"): o BKO confere se é verdade antes de tratar o pedido.
   *  'pendente' = aguarda BKO · 'validado' = ok · 'reprovado' = vai p/ cancelado.
   *  null = não precisa (gerente já decidiu, ou convergência real móvel+fibra). */
  bko_validacao?: 'pendente' | 'validado' | 'reprovado' | null;
  bko_validado_por?: string | null;
  bko_validado_em?: string | null;
  bko_motivo?: string | null;
  bko_resultado?: 'validar_declaracao' | 'pune' | 'nao_pune' | 'tabela_1' | 'tabela_2' | null;
  regularizar_tabela?: boolean;
  /** Print da consulta de cobertura anexado pelo consultor ao declarar
   *  "não tem viabilidade de fibra" (prova de que NÃO tem cobertura no
   *  endereço da Receita, contra o que o sistema/Claro informa). */
  prova_url?: string | null;     // primeiro print (compat)
  prova_urls?: string[];         // todos os prints anexados
  /** Consultor confirmou que o print é do endereço oficial (Receita Federal). */
  prova_confirmou_rf?: boolean | null;
  /** Justificativa ADICIONAL do supervisor (opcional) ao analisar o caso. */
  justificativa_supervisor?: string | null;
  supervisor_id?: string | null;
  supervisor_em?: string | null;
  /** Motivo escrito pela gerência ao aprovar/reprovar. */
  motivo_gerente?: string | null;
  /** Rodada atual do ciclo (1 = 1º envio; incrementa a cada reenvio do consultor). */
  rodada?: number;
  /** Marcado quando BKO/gerência valida o "sem cobertura" com cobertura
   *  informada pela Claro = TEM → caso de divergência p/ cobrar a Claro. */
  divergencia_claro?: boolean | null;
}

export function montarConvergencia(args: {
  fatos: FatosCnpj;
  cobertura: CoberturaCnpj;
  classificacao: ClassificacaoConvergencia;
  status: StatusConvergencia | null;
  origem: ConvergenciaPayload['origem_decisao'];
  resolucao?: ResolucaoModal | null;
  decididoPor: string | null;
  agora: string; // ISO (passar de fora — sem Date.now() aqui)
  /** Convergência anterior do pedido (p/ ciclo de reenvio: incrementa a rodada). */
  anterior?: ConvergenciaPayload | null;
}): ConvergenciaPayload {
  const { fatos, cobertura, classificacao, status, origem, resolucao, decididoPor, agora, anterior } = args;

  // Ciclo de reenvio: se a rodada anterior foi reprovada/devolvida, esta é uma rodada nova.
  // O histórico de cada escrita é gravado na timeline `movimento`, não aqui.
  const reprovouAntes = anterior?.status === 'reprovado gerente' || anterior?.status === 'reprovado supervisor';
  const rodada = anterior ? ((anterior.rodada || 1) + (reprovouAntes ? 1 : 0)) : 1;

  return {
    rodada,
    classificacao,
    status,
    motivo: resolucao?.motivo ?? null,
    motivo_tipo: resolucao?.motivoTipo ?? (origem === 'auto' ? 'automatico' : null),
    tem_movel: fatos.temMovel,
    tem_fibra: fatos.temFibra,
    cobertura_fibra: cobertura.temCobertura,
    cep: cobertura.cep,
    territorio_aace: cobertura.territorioAace,
    territorio_nosso: cobertura.territorioNosso,
    qtd_linhas_movel: resolucao?.qtdLinhasMovel ?? cobertura.qtdLinhasVoz ?? null,
    cnpj_categoria: fatos.cnpjCategoria ?? null,
    movel_novo_base: fatos.movelNovoBase ?? null,
    fixa_novo_base: resolucao?.fixaJaExistente ? 'CONTRATO_EXISTENTE' : (fatos.fixaNovoBase ?? null),
    fixa_ja_existente: resolucao?.fixaJaExistente ?? null,
    decidido_por: decididoPor,
    decidido_em: agora,
    origem_decisao: origem,
    // BKO valida: (a) o que o consultor AUTO-DECLAROU (já tem fibra/móvel) e
    // (b) o "não tem viabilidade de fibra" com print anexado (prova de sem
    // cobertura) — esse vai pro BKO E pra gerência/supervisor ao mesmo tempo.
    bko_validacao:
      origem === 'modal_cliente_tem_fibra' || origem === 'modal_informou_movel' || !!resolucao?.provaUrl || !!resolucao?.provaUrls?.length
        ? 'pendente'
        : null,
    bko_validado_por: null,
    bko_validado_em: null,
    bko_motivo: null,
    prova_url: resolucao?.provaUrls?.[0] ?? resolucao?.provaUrl ?? null,
    prova_urls: resolucao?.provaUrls ?? (resolucao?.provaUrl ? [resolucao.provaUrl] : []),
    prova_confirmou_rf: resolucao?.confirmouRf ?? null,
    justificativa_supervisor: null,
    divergencia_claro: null,
  };
}
